// When is it worth reading /api/resolve/report for a contract?
//
// The read hits BigQuery, so it is deliberately gated rather than fired on every
// report open. The gate used to be `contractStatus === "resolved"`, which was exactly
// right while the endpoint served conclusive attempts only: an unresolved contract had
// nothing to fetch.
//
// The endpoint now also serves a settled `unresolved` attempt (its open_reasons /
// missing_evidence explain a non-verdict), so a contract that is NOT `resolved` can
// have something worth reading. But not always — and the extra condition is not a
// guess, it is the modal's own render gate: a non-conclusive verdict is only shown
// once the contract's deadline has PASSED, because "Unresolved" before the question's
// window has even closed isn't informative.
//
// So the rule is: read exactly when the render gate could pass. That keeps the case
// the old gate protected — opening a report on a live, in-window forecast — dark, with
// no added BigQuery traffic, while making the new payload reachable where it can
// actually be displayed. Sharing one predicate is the point: if the fetch gate were
// looser the read would be wasted, and if it were tighter the section would render a
// verdict it could not explain.

import { parseResolveDateLocal } from "../formatDates.js";
import { isConclusiveResolutionStatus, resolutionHasBody } from "./resolutionBody.js";

/**
 * Whether a resolution read is worth issuing for this contract.
 *
 * @param {{contractStatus?: string|null, resolutionDate?: string|null,
 *   today?: Date|number|null, storedResolution?: object|null}} args
 *   `resolutionDate` is the contract DEADLINE (`contract.resolution_date`), not a
 *   verdict date; `today` is the app's `effectiveToday` (mock-clock aware);
 *   `storedResolution` is the row's own `active.resolution` off the wire.
 * @returns {boolean}
 */
export function resolutionReadWorthwhile({
  contractStatus,
  resolutionDate,
  today,
  storedResolution,
} = {}) {
  // ALREADY IN HAND — the decisive check, and it must come first. The server caches a
  // fetched report onto the row (server/lib/persistResolutionReport.js), so once a body
  // is on the wire there is nothing left to read: only CONCLUSIVE reports are cached,
  // and a conclusive verdict is terminal, so it cannot go stale. Without this the
  // ~1s BigQuery read would still fire on every open — the cache would hide the
  // latency without removing the query, which was the point.
  //
  // Assumes a stored body is a COMPLETE one. True of all three writers: the notifier
  // stamps verdict + dates and no body, while the resolve-poll round-trip and the
  // write-back both store a whole report.
  if (resolutionHasBody(storedResolution)) return false;
  // A resolved or voided contract always has a conclusive verdict to fetch, and the render
  // gate shows a conclusive one before the deadline too. A void event clears the stored
  // resolution, so an early void has nothing in hand without this read.
  if (contractStatus === "resolved" || contractStatus === "voided") return true;
  // An on-demand verdict lands only on the row's stored resolution — the poll writes the flat
  // verdict and leaves the contract open — so a stored conclusive verdict with no body is read
  // too, as the render gate shows it whatever the deadline.
  if (isConclusiveResolutionStatus(storedResolution?.status)) return true;
  // Otherwise only once the deadline has passed — the same condition that lets the
  // modal show a non-conclusive verdict. An absent/unparseable deadline reads as "not
  // yet past", matching the render gate's null handling (it declines to show).
  if (!resolutionDate) return false;
  const deadline = parseResolveDateLocal(resolutionDate);
  if (!deadline || isNaN(deadline.getTime())) return false;
  const nowMs = today instanceof Date ? today.getTime() : typeof today === "number" ? today : null;
  if (nowMs == null) return false;
  return deadline.getTime() < nowMs;
}
